import { createFullPresetExtensions, Extension } from "@smartsuite/smartdoc";
import { useMemo } from "react";

import { type MentionAttrs, recordMentionUrl, useFormAccountId } from "./publicMentions";

type Nodes = Parameters<Extension["addNodes"]>[0];

/**
 * Shows mentions as a public form does: a record mention is a link to the
 * record that opens in a new tab, and a member or team mention is its text.
 */
export class PublicMentionExtension extends Extension {
  constructor(private readonly accountId?: string) {
    super();
  }

  addNodes(nodes: Nodes): Nodes {
    return nodes.append({
      mention: {
        attrs: { id: {}, application: {}, title: {}, prefix: {} },
        inline: true,
        group: "inline",
        selectable: false,
        toDOM: (node) => {
          const attrs = node.attrs as MentionAttrs;
          const text = `${attrs.prefix ?? ""}${attrs.title ?? ""}`;
          const href = recordMentionUrl(this.accountId, attrs);
          return href
            ? ["a", { class: "mention", href, target: "_blank", rel: "noopener noreferrer" }, text]
            : ["span", { class: "mention" }, text];
        },
      },
    });
  }
}

/** The extensions a form renders a stored document with. */
export const usePublicEditorExtensions = (base: () => Extension[]): Extension[] => {
  const accountId = useFormAccountId();
  return useMemo(() => [...base(), new PublicMentionExtension(accountId)], [base, accountId]);
};

export const fullPresetExtensions = (): Extension[] => createFullPresetExtensions();
